/* eslint-disable @typescript-eslint/naming-convention */

import ExclamationTriangle from "@/components/core/badges/ExclamationTriangle";
import { requestGetCertificateQcpInformation } from "@/state/qcpCertificate/QCPCertificateEffects";
import { Field } from "@/type/core/form/field.types";
import { QcpCertificateInformation } from "@/type/core/qcp/qcp.types";
import { getJseAuthCookie } from "@/utils/helpers/api.helper";
import BasicButton from "@components/core/buttons/BasicButton";
import InputFormField from "@components/core/inputs/InputFormField";
import MailFormField from "@components/core/inputs/MailFormField";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import {
  faEnvelope,
  faExclamationTriangle,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ROUTES } from "@routes/Routes";
import { setOnboardingToFalse } from "@state/auth/AuthEvents";
import {
  requestGetUserInformationHistory,
  requestOnboardingLogout,
  requestRevokeCertificateForRenewNow,
  requestSetConsentDate,
  requestSetLastUseOfOldValue,
  requestUpdateUserWithNewValue,
} from "@state/onboarding/OnboardingEffects";
import {
  CERTIFICATE_REVOKE_REASON,
  formValidateTriggers,
  ONBOARDING_NEW_INFORMATION_TIMEOUT_MINUTES,
} from "@utils/Constant";
import { toastError } from "@utils/helpers/toast-helper";
import { Col, Form, Row } from "antd";
import { useForm } from "antd/lib/form/Form";
import parse from "html-react-parser";
import {
  Dispatch,
  FunctionComponent,
  ReactElement,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
export interface UserInformationHistory {
  user_id?: number | null;
  new_email?: string;
  old_email?: string;
  new_lastname?: string;
  old_lastname?: string;
  new_firstname?: string;
  old_firstname?: string;
  treated?: boolean;
  old_value?: string;
  new_identity_usability?: string;
  consent_date?: string;
  last_use_of_old_value?: string;
}

interface OBNewInformationPageProps {
  certificateDegradedMode: boolean;
  setLoading: (loading: boolean) => void;
  isReconnectionNeededModalOpen: boolean;
  setIsReconnectionNeededModalOpen: Dispatch<SetStateAction<boolean>>;
  isConsentRevocationModalOpen: boolean;
  setIsConsentRevocationModalOpen: Dispatch<SetStateAction<boolean>>;
  className?: string;
}

const OBNewInformationPage: FunctionComponent<OBNewInformationPageProps> = ({
  certificateDegradedMode,
  setLoading,
  isReconnectionNeededModalOpen,
  setIsReconnectionNeededModalOpen,
  isConsentRevocationModalOpen,
  setIsConsentRevocationModalOpen,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = useForm();

  const arrivalOnPageTimeRef = useRef<Date>(new Date());

  const [qcp, setQcp] = useState<QcpCertificateInformation>();
  const [fields, setFields] = useState<Field[]>([]);
  const [userInformationHistory, setUserInformationHistory] =
    useState<UserInformationHistory>({
      user_id: null,
      new_email: "",
      old_email: "",
      new_lastname: "",
      old_lastname: "",
      treated: false,
      old_value: "",
      new_identity_usability: "",
      consent_date: "",
      last_use_of_old_value: "",
    });

  useEffect(() => {
    void requestGetCertificateQcpInformation({});
    void requestGetUserInformationHistory({});
  }, []);

  useEffect(() => {
    setFields([
      {
        name: ["old_firstname"],
        value: userInformationHistory.old_firstname,
      },
      {
        name: ["old_lastname"],
        value: userInformationHistory.old_lastname,
      },
      {
        name: ["old_email"],
        value: userInformationHistory.old_email,
      },
      {
        name: ["new_firstname"],
        value: userInformationHistory.new_firstname,
      },
      {
        name: ["new_lastname"],
        value: userInformationHistory.new_lastname,
      },
      {
        name: ["new_email"],
        value: userInformationHistory.new_email,
      },
    ]);
  }, [userInformationHistory]);

  useEffect(() => {
    return requestGetCertificateQcpInformation.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setQcp(result.data);
      }
    });
  });

  useEffect(() => {
    return requestGetUserInformationHistory.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setUserInformationHistory(result.data);
      } else {
        toastError(t("onboarding.newInformation.userInformationsError"));
      }
    });
  }, []);

  const handleConfirm = (): void => {
    setLoading(true);
    void requestSetConsentDate({
      dto: {
        consent_date: new Date(),
      },
    }).then((res) => {
      if (!res.ok) {
        toastError(t("onboarding.newInformation.setConsentDateError"));
      }
    });
    if (
      userInformationHistory &&
      userInformationHistory.old_value === "VALID"
    ) {
      if (!certificateDegradedMode) {
        void requestRevokeCertificateForRenewNow({
          dto: {
            status: CERTIFICATE_REVOKE_REASON.CERTIFICATE_REVOKED_INFORMATION,
          },
        }).then((res) => {
          if (res.ok) {
            void requestUpdateUserWithNewValue({ dto: {} });
          } else {
            toastError(t("onboarding.renewUsCertificate.modal.revokeError"));
          }
        });
      } else {
        setIsConsentRevocationModalOpen(false);
        setLoading(false);
      }
    } else {
      void requestUpdateUserWithNewValue({ dto: {} });
    }
  };

  const handleUseNewValues = () => {
    const authCookie = getJseAuthCookie();
    const now = new Date();
    const elapsedMs = now.getTime() - arrivalOnPageTimeRef.current.getTime();

    if (!authCookie || elapsedMs > ONBOARDING_NEW_INFORMATION_TIMEOUT_MINUTES) {
      setIsReconnectionNeededModalOpen(true);
    } else {
      if (
        userInformationHistory &&
        userInformationHistory.old_value === "VALID"
      ) {
        if (!certificateDegradedMode) {
          setIsConsentRevocationModalOpen(true);
        }
      } else {
        void requestUpdateUserWithNewValue({ dto: {} });
      }
    }
  };

  const getForm = (newOrOld: string): ReactElement => {
    return (
      <Row gutter={30} className="form-onboarding justify-content-center my-48">
        <Col span={24} md={18} lg={12}>
          <Form form={form} fields={fields} {...formValidateTriggers}>
            {userInformationHistory.new_firstname && (
              <Row gutter={30} className="align-items-center mb-16">
                <Col className="form-onboarding-label" span={20} md={8}>
                  <label
                    htmlFor={
                      newOrOld === "new" ? "new_firstname" : "old_firstname"
                    }
                    data-test="input-label"
                    data-alt={
                      t("onboarding.home.label.firstname") + ` (${newOrOld})`
                    }
                    data-test-for={`${newOrOld}_firstname`}
                  >
                    {t("onboarding.home.label.firstname")}
                  </label>
                </Col>
                <Col className="form-onboarding-icon" span={4} md={2}>
                  <FontAwesomeIcon icon={faUser} />
                </Col>
                <Col className="form-onboarding-input" span={24} md={14}>
                  <InputFormField
                    disabled
                    module="onboarding"
                    field={
                      newOrOld === "new" ? "new_firstname" : "old_firstname"
                    }
                    placeholder=" "
                    className="mb-0"
                  />
                </Col>
              </Row>
            )}
            {userInformationHistory.new_lastname && (
              <Row gutter={30} className="align-items-center mb-16">
                <Col className="form-onboarding-label" span={20} md={8}>
                  <label
                    htmlFor={
                      newOrOld === "new" ? "new_lastname" : "old_lastname"
                    }
                    data-test="input-label"
                    data-alt={
                      t("onboarding.home.label.lastname") + ` (${newOrOld})`
                    }
                    data-test-for={`${newOrOld}_lastname`}
                  >
                    {t("onboarding.home.label.lastname")}
                  </label>
                </Col>
                <Col className="form-onboarding-icon" span={4} md={2}>
                  <FontAwesomeIcon icon={faUser} />
                </Col>
                <Col className="form-onboarding-input" span={24} md={14}>
                  <InputFormField
                    disabled
                    module="onboarding"
                    field={newOrOld === "new" ? "new_lastname" : "old_lastname"}
                    placeholder=" "
                    className="mb-0"
                  />
                </Col>
              </Row>
            )}
            {userInformationHistory.new_email && (
              <Row gutter={30} className="align-items-center mb-16">
                <Col className="form-onboarding-label" span={20} md={8}>
                  <label
                    htmlFor={newOrOld === "new" ? "new_email" : "old_email"}
                    data-test="input-label"
                    data-alt={
                      t("onboarding.home.label.email") + ` (${newOrOld})`
                    }
                    data-test-for={`${newOrOld}_email`}
                  >
                    {t("onboarding.home.label.email")}
                  </label>
                </Col>
                <Col className="form-onboarding-icon" span={4} md={2}>
                  <FontAwesomeIcon icon={faEnvelope} />
                </Col>
                <Col className="form-onboarding-input" span={24} md={14}>
                  <MailFormField
                    disabled
                    module="onboarding"
                    field={newOrOld === "new" ? "new_email" : "old_email"}
                    placeholder=" "
                    className="mb-0"
                  />
                </Col>
              </Row>
            )}
          </Form>
        </Col>
      </Row>
    );
  };

  return (
    <div className={`app-onboarding onboarding-new-information ${className}`}>
      <p>{t("onboarding.newInformation.changes")}</p>
      {qcp !== undefined && (
        <p className="white-space-pre-line">
          {t("account.mySignature.certificate.qcp.informations", {
            firstname: qcp.firstName,
            lastname: qcp.lastName,
            phone: qcp.phone,
            email: qcp.email,
          })}
        </p>
      )}
      <h3>{t("onboarding.newInformation.old_information")}</h3>
      {userInformationHistory &&
        userInformationHistory.old_value === "VALID" &&
        t("onboarding.newInformation.oldValue.valid")}
      {getForm("old")}
      <h3>{t("onboarding.newInformation.new_information")}</h3>
      {getForm("new")}
      {userInformationHistory &&
        userInformationHistory.new_identity_usability !== "CAN_BE_USED" && (
          <p className="text-danger mb-48" data-test="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-8" />
            {userInformationHistory.new_identity_usability ===
              "HAS_CERTIFICATE_IN_OTHER_ORG" &&
              parse(
                t(
                  "onboarding.newInformation.new_identity.has_certificate_in_other_org",
                ),
              )}
            {userInformationHistory.new_identity_usability ===
              "HAS_CERTIFICATE_IN_JSE_ORG" &&
              parse(
                t(
                  "onboarding.newInformation.new_identity.has_certificate_in_jse_org",
                ),
              )}
          </p>
        )}
      <h3>{t("onboarding.newInformation.choice")}</h3>
      <div className="d-flex justify-content-between align-items-center">
        {userInformationHistory &&
          userInformationHistory.old_value === "VALID" && (
            <BasicButton
              variant="tertiary"
              text={t("onboarding.newInformation.keepUsingOldValue")}
              onClick={() => {
                void requestSetLastUseOfOldValue({
                  dto: {
                    last_use_of_old_value: new Date(),
                  },
                }).then((res) => {
                  if (!res.ok) {
                    toastError(t("onboarding.newInformation.setLastUseError"));
                  }
                });
                setOnboardingToFalse();
                navigate(ROUTES.extranet.home.generate());
              }}
            />
          )}
        <BasicButton
          disabled={
            (userInformationHistory &&
              userInformationHistory.old_value === "VALID" &&
              certificateDegradedMode) ||
            userInformationHistory.new_identity_usability !== "CAN_BE_USED"
          }
          variant="tertiary"
          text={t("onboarding.newInformation.useNewValue")}
          onClick={handleUseNewValues}
        />
      </div>
      {certificateDegradedMode && (
        <ExclamationTriangle
          text={t("account.mySignature.certificate.unavailable")}
          className="mt-24"
        />
      )}
      <ConfirmModal
        visible={isConsentRevocationModalOpen}
        onCancel={() => setIsConsentRevocationModalOpen(false)}
        onOk={handleConfirm}
        title={t("onboarding.newInformation.modal.title")}
        content={parse(t("onboarding.newInformation.modal.body"))}
        okText={t("onboarding.newInformation.modal.yesActionLabel")}
        cancelText={t("onboarding.newInformation.modal.noActionLabel")}
      />
      <ConfirmModal
        visible={isReconnectionNeededModalOpen}
        onCancel={() => setIsReconnectionNeededModalOpen(false)}
        onOk={() => {
          if (!certificateDegradedMode) {
            void requestOnboardingLogout({});
            setIsReconnectionNeededModalOpen(false);
          } else {
            setIsReconnectionNeededModalOpen(false);
          }
        }}
        title={t(
          "account.mySignature.certificate.modal.reconnectionNeeded.title",
        )}
        content={parse(
          t("account.mySignature.certificate.modal.reconnectionNeeded.body"),
        )}
        okText={t(
          "account.mySignature.certificate.modal.reconnectionNeeded.yesActionLabel",
        )}
        cancelText={t(
          "account.mySignature.certificate.modal.reconnectionNeeded.noActionLabel",
        )}
      />
    </div>
  );
};

export default OBNewInformationPage;
